import React, { createContext, useEffect, useState, useRef, useMemo, useContext } from 'react';
// import { adSVC as Ads } from '@com/core/common/services/api';
import isEqual from 'lodash/isEqual';
import Axios from 'axios';
import { TTimer } from '../types/ads';
import { reportEvent } from '../utils/sentry';
import { TAdBannerModule, TCatalogResponse, TModuleGroup } from '../types/catalog';
import { AppContext } from './appContext';
import { getAppConfig } from '../utils/config';

type TProps = {
    children: JSX.Element;
    catalog: TCatalogResponse;
};

type TSlots = Record<
    string,
    {
        clickUrls: string[];
        id: string;
        impUrls: string[];
        imageUrl: string;
        viewUrls: string[];
        linkUrl: string;
        impressionSent?: boolean;
    }
>;

type TAdContext = {
    // states
    adObserver: any;
    fetched: boolean;

    // functions
    trackImpression: (slotId: string | number) => void;
    trackAdClick: (slotId: string | number) => void;
    getUrl: (slotId: string | number) => string;
    getLinkUrl: (slotId: string | number) => string;
};

const initState = {
    // states
    adObserver: null,
    fetched: false,

    // functions
    trackImpression: (): void => undefined,
    trackAdClick: (): void => undefined,
    getUrl: (): string => '',
    getLinkUrl: (): string => '',
};

export const AdContext = createContext<TAdContext>(initState);

export const AdContextProvider = ({ catalog, children }: TProps): JSX.Element => {
    let adSlotList = useMemo(() => [], []);
    const [slots, setSlots] = useState<TSlots>({});
    const [fetched, setFetched] = useState(false);
    const adObserver = useRef(null);
    const [, setTimers] = useState<TTimer>({});
    const { locale } = useContext(AppContext);

    // Capture adSlotList to use for ads
    const { type } = catalog;
    try {
        if (type === 'static' || type === 'catalog') {
            let moduleGroups: TModuleGroup[];

            if (catalog.type === 'static') {
                moduleGroups = catalog.data;
            } else {
                moduleGroups = catalog.cms.data || [];
            }

            // collect adSlots to adSlotList[]
            moduleGroups.forEach(moduleGroup => {
                if (moduleGroup.modules) {
                    moduleGroup.modules.forEach(module => {
                        if (Array.isArray((module as TAdBannerModule).banners)) {
                            (module as TAdBannerModule).banners.forEach(banner => {
                                if (banner.slotId) {
                                    adSlotList.push(banner.slotId);
                                }
                            });
                        }
                    });
                }
            });
        }
    } catch (e) {
        reportEvent('Error getting adSlotList', '_app.render', 'error', { e });
    }

    adSlotList = [...new Set(adSlotList)];

    useEffect(() => {
        if (!Array.isArray(adSlotList) || (Array.isArray(adSlotList) && adSlotList.length === 0)) {
            // Do nothing
        } else {
            const fetchAdsAndObserve = async () => {
                const newSlots = {};
                setSlots({});
                setFetched(false);
                const appConfig = await getAppConfig();

                try {
                    const response = await Axios.post(
                        '/_svc/ads-api/get-ads',
                        { slotList: adSlotList },
                        {
                            headers: {
                                ...appConfig.commonHeaders,
                                'X-Locale': locale.apiLocale,
                            },
                        },
                    );
                    // Below gets queued unnecessarily via Axios. Not fixed yet.
                    // const response = await Ads(locale.apiLocale).getAds(adSlotList);

                    adSlotList.forEach(slotId => {
                        try {
                            newSlots[slotId] = response.data.slots.find(slot => slot.id === slotId);
                        } catch (error) {
                            reportEvent(
                                'No matching slotId in get-ads response',
                                'adContext.fetchAdsAndObserve',
                                'error',
                                {
                                    error,
                                },
                            );
                        }
                    });

                    setSlots(newSlots);
                    setFetched(true);
                } catch (error) {
                    setSlots({});
                    setFetched(true);
                    reportEvent('Error fetching ads', 'adContext.fetchAdsAndObserve', 'error', { error });
                }

                const observer = new IntersectionObserver(
                    entries => {
                        entries.forEach(entry => {
                            try {
                                setTimers(timerList => {
                                    const { slotid } = entry.target.dataset;
                                    const timer = timerList[slotid] || {
                                        total: 0,
                                        start: null,
                                        previouslyVisible: false,
                                    };

                                    if (entry.isIntersecting) {
                                        // Start the timer
                                        timer.start = new Date();
                                    } else if (timer.start) {
                                        // Stop the timer and add to the total
                                        const startTime = timer.start.getTime();
                                        timer.total += new Date().getTime() - startTime;
                                        timer.start = null;

                                        // Swiper slide duplicate fix
                                        if (
                                            entry.target.parentElement &&
                                            entry.target.parentElement.className.includes('swiper-slide')
                                        ) {
                                            timer.total += 900;
                                        }

                                        if (timer.total >= 1000) {
                                            trackView(entry.target.dataset.slotid, newSlots, startTime);
                                            const newTimers = { ...timerList };
                                            delete newTimers[slotid];
                                            return newTimers;
                                        }
                                    }

                                    const newTimerList = { ...timerList, [slotid]: timer };
                                    if (!isEqual(timerList, newTimerList)) {
                                        return newTimerList;
                                    }
                                    return timerList;
                                });
                            } catch (e) {
                                reportEvent('Error observing ad view', 'adContext.observer', 'error', { e });
                            }
                        });
                    },
                    {
                        threshold: 0.5,
                    },
                );
                adObserver.current = observer;
            };
            fetchAdsAndObserve();
        }
        return () => {
            if (adObserver.current) {
                adObserver.current.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalog]);

    useEffect(() => {
        let handleVisibilityChange;
        if (!Array.isArray(adSlotList) || (Array.isArray(adSlotList) && adSlotList.length === 0)) {
            // Do nu'n
        } else {
            handleVisibilityChange = () => {
                try {
                    if (document.hidden) {
                        setTimers(timerList => {
                            const newTimers = { ...timerList };

                            Object.keys(timerList).forEach(slotId => {
                                const timer = newTimers[slotId];
                                if (timer.start) {
                                    // Stop the timer and add to the total
                                    const startTime = timer.start.getTime();
                                    timer.total += new Date().getTime() - startTime;
                                    timer.start = null;
                                    timer.previouslyVisible = true;

                                    if (timer.total >= 1000) {
                                        trackView(slotId, slots, startTime);
                                        delete newTimers[slotId];
                                    }
                                }
                            });
                            return newTimers;
                        });
                    } else {
                        // When the page comes back into view, we need to start timers if the ads are in view
                        setTimers(timerList => {
                            const newTimers = { ...timerList };

                            Object.keys(timerList).forEach(slotId => {
                                const timer = newTimers[slotId];
                                if (timer.previouslyVisible) {
                                    timer.start = new Date();
                                }
                            });
                            return newTimers;
                        });
                    }
                } catch (e) {
                    reportEvent('Error observing ad visibility change', 'adContext.handleVisibilityChange', 'error', {
                        e,
                    });
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);
        }
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slots]);

    const trackView = (slotId: string | number, _slots: TSlots, startTime: number): void => {
        try {
            _slots[slotId].viewUrls.forEach(viewUrl => {
                const startTimeEpoch = Math.round(new Date(startTime).getTime()).toString();
                const endTimeEpoch = Math.round(new Date().getTime()).toString();

                let formattedUrl = viewUrl;
                formattedUrl = formattedUrl.replace('[STARTTIME]', startTimeEpoch);
                formattedUrl = formattedUrl.replace('[ENDTIME]', endTimeEpoch);

                const pixel = new Image();
                pixel.src = formattedUrl;
            });
        } catch (e) {
            reportEvent('Error tracking view', 'adContext.trackView', 'error', { e });
        }
    };

    const trackImpression = (slotId: string | number): void => {
        try {
            if (slots[slotId] && !slots[slotId].impressionSent) {
                slots[slotId].impUrls.forEach(impUrl => {
                    const pixel = new Image();
                    pixel.src = impUrl;
                });

                setSlots(_slots => {
                    const newSlots = { ..._slots };

                    if (newSlots[slotId]) {
                        newSlots[slotId].impressionSent = true;
                    }

                    return newSlots;
                });
            }
        } catch (e) {
            reportEvent('Error tracking impression', 'adContext.trackImpression', 'error', { e });
        }
    };

    const trackAdClick = (slotId: string | number): void => {
        try {
            slots[slotId].clickUrls.forEach(clickUrl => {
                const eventTime = Math.round(new Date().getTime()).toString();

                let formattedUrl = clickUrl;
                formattedUrl = formattedUrl.replace('[EVENTTIME]', eventTime);

                const pixel = new Image();
                pixel.src = formattedUrl;
            });
        } catch (e) {
            reportEvent('Error tracking click', 'adContext.trackAdClick', 'error', { e });
        }
    };

    const getUrl = (slotId: string | number): string => {
        try {
            if (slots && slots[slotId]) {
                return slots[slotId].imageUrl;
            }
        } catch (e) {
            reportEvent('Error getting url for ad', 'adContext.getUrl', 'error', { e });
        }
        return '';
    };

    const getLinkUrl = (slotId: string | number): string => {
        try {
            if (slots && slots[slotId]) {
                return slots[slotId].linkUrl;
            }
        } catch (e) {
            reportEvent('Error getting link url for ad', 'adContext.getLinkUrl', 'error', { e });
        }

        return '';
    };

    // states
    const contextStates = {
        adObserver,
        fetched,
    };

    // functions
    const contextFunctions = {
        trackImpression,
        trackAdClick,
        getUrl,
        getLinkUrl,
    };

    return <AdContext.Provider value={{ ...contextStates, ...contextFunctions }}>{children}</AdContext.Provider>;
};
